// Input rules. The app checks them for quick feedback; the database repeats every rule
// (see supabase/migrations), so a modified app can never bypass them.
import { z } from 'zod';
import { FIRST_YEAR, LAST_YEAR } from './years';
import { RAINBOW } from './text';

export const BRANCHES = ['Songs', 'Artists', 'Gaming', 'Seasons', 'TV & Cartoons', 'Movies', 'Internet', 'School', 'Toys', 'Tech', 'Fashion', 'Kindness'] as const;
export type Branch = (typeof BRANCHES)[number];
export const BRANCH_COLOR: Record<Branch, string> = {
  Songs: 'pink', Artists: 'violet', Gaming: 'mint', Seasons: 'amber', 'TV & Cartoons': 'sky', Movies: 'coral',
  Internet: 'periwinkle', School: 'yellow', Toys: 'bubblegum', Tech: 'aqua', Fashion: 'lime', Kindness: 'orange',
};

const year = z.number().int().min(FIRST_YEAR, `Pick a year from ${FIRST_YEAR}`).max(LAST_YEAR, `Pick a year up to ${LAST_YEAR}`);
const clean = (min: number, max: number, what: string) =>
  z.string().trim().min(min, `${what} needs at least ${min} characters`).max(max, `${what} can be at most ${max} characters`);

export const PostInput = z.object({
  title: clean(2, 80, 'The name'),
  body: clean(1, 1000, 'The memory'),
  year,
  branch: z.enum(BRANCHES),
  format: z.enum(['normal', 'forgotten', 'there']),
  lived: z.boolean(),
  core: z.boolean(),
  communityId: z.string().optional(),
  ideaId: z.string().optional(),
  passedFromId: z.string().optional(),
});
export type PostInput = z.infer<typeof PostInput>;

export const CommentInput = z.object({ body: clean(1, 500, 'The comment') });

export const HANDLE_RE = /^[a-z0-9._]{3,20}$/;
export const ProfileInput = z.object({
  handle: z.string().trim().toLowerCase().regex(HANDLE_RE, 'Use 3 to 20 letters, numbers, dots or underscores'),
  name: clean(1, 40, 'The name'),
  bio: z.string().trim().max(160, 'The bio can be at most 160 characters'),
  color: z.enum(RAINBOW),
  away: z.string().trim().max(80, 'The away message can be at most 80 characters'),
  favoriteYear: year.optional(),
});
export type ProfileInput = z.infer<typeof ProfileInput>;

export const CommunityInput = z.object({
  name: clean(3, 40, 'The name'),
  description: clean(10, 200, 'The description'),
  rules: z.array(clean(3, 120, 'A rule')).max(5, 'At most 5 rules'),
});
export type CommunityInput = z.infer<typeof CommunityInput>;

export const IdeaInput = z.object({ text: clean(10, 140, 'The idea'), year: year.optional() });
export type IdeaInput = z.infer<typeof IdeaInput>;

export const REPORT_REASONS = ['Spam', 'Harassment or bullying', 'About me, without my consent', 'A photo of me or my child', 'Hate', 'Sexual content', 'Violence or threats', 'Copyright', 'Something else illegal', 'Other'] as const;
export const ReportInput = z.object({ reason: z.enum(REPORT_REASONS), details: z.string().trim().max(1000) });

/** First error message per field, for showing next to inputs. */
export function fieldErrors(err: z.ZodError): Record<string, string> {
  const out: Record<string, string> = {};
  for (const i of err.issues) { const k = String(i.path[0] ?? 'form'); if (!out[k]) out[k] = i.message; }
  return out;
}
